import { Database, Loader2, Search, Store } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { FoodSearchResult, KaloriShop } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import { useFoodSearch } from '../hooks/use-food-search'
import { useLoadMoreOnScroll } from '../hooks/use-load-more-on-scroll'
import { dbCartKey, type FoodCartItem, toggleByKey } from './cart-utils'
import { ShopPickerView } from './shop-picker-view'

type Props = {
  cart: FoodCartItem[]
  onCartChange: (cart: FoodCartItem[]) => void
  /** 検索結果の最大高さ（Tailwind のクラス） */
  listMaxHeight?: string
  /** 見出しを出さない（タブ名で分かるとき） */
  hideHeading?: boolean
  /** 開いたときにこの文字で検索しておく（2 文字以上のとき） */
  initialQuery?: string
}

export function FoodSearchSection({
  cart,
  onCartChange,
  listMaxHeight = 'max-h-52',
  hideHeading = false,
  initialQuery = '',
}: Props) {
  const [query, setQuery] = useState(initialQuery)
  const [selectedShop, setSelectedShop] = useState<KaloriShop | null>(null)
  const [mode, setMode] = useState<'search' | 'shop-picker'>('search')
  const {
    data: searchResults,
    isLoading: isSearching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    trigger: triggerSearch,
  } = useFoodSearch()

  const cartKeys = useMemo(() => new Set(cart.map(dbCartKey)), [cart])

  useEffect(() => {
    triggerSearch(initialQuery, null)
  }, [initialQuery, triggerSearch])

  // 栄養値 null の除外で 1 ページ目が空でも続きがあればリスト（と番兵）を出す
  const showResults = !!searchResults && (searchResults.length > 0 || hasNextPage)
  const { scrollRef, sentinelRef } = useLoadMoreOnScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    watchKey: `${showResults}-${mode}`,
  })

  const handleSearch = useCallback(() => {
    triggerSearch(query, selectedShop?.query ?? null)
  }, [query, selectedShop, triggerSearch])

  const handleShopSelect = useCallback(
    (shop: KaloriShop | null) => {
      setSelectedShop(shop)
      const trimmed = query.trim()
      if (trimmed.length >= 2) {
        triggerSearch(trimmed, shop?.query ?? null)
      }
    },
    [query, triggerSearch],
  )

  const handleToggle = useCallback(
    (food: FoodSearchResult) => {
      const { food_name, shop_name, calories, protein_g, fat_g, carb_g } = food
      onCartChange(toggleByKey(cart, { food_name, shop_name, calories, protein_g, fat_g, carb_g }, dbCartKey))
    },
    [cart, onCartChange],
  )

  if (mode === 'shop-picker') {
    return (
      <div className="space-y-2">
        <h4 className="flex items-center gap-1 text-xs font-medium">
          <Store className="size-3 text-emerald-400" />
          ショップで絞り込み
        </h4>
        <ShopPickerView selectedShop={selectedShop} onSelect={handleShopSelect} onBack={() => setMode('search')} />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {!hideHeading && (
        <h4 className="flex items-center gap-1 text-xs font-medium">
          <Database className="size-3 text-emerald-400" />
          食品データベース検索
        </h4>
      )}

      {/* 検索入力 + ショップ絞り込みボタン */}
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="食品名・店名を入力..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch()
            }}
            className="h-8 pl-8 text-sm"
          />
        </div>
        <Button
          type="button"
          variant={selectedShop ? 'default' : 'outline'}
          size="sm"
          className="h-8 shrink-0 gap-1 px-2"
          onClick={() => setMode('shop-picker')}
          title={selectedShop ? `${selectedShop.name} で絞り込み中` : 'ショップで絞り込み'}
        >
          <Store className="size-3.5" />
          <span className="max-w-24 truncate text-xs">{selectedShop ? selectedShop.name : 'ショップ'}</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 px-3"
          onClick={handleSearch}
          disabled={query.trim().length < 2 || isSearching}
        >
          {isSearching ? <Loader2 className="size-3.5 animate-spin" /> : '検索'}
        </Button>
      </div>

      {/* 検索結果 */}
      {showResults && (
        <div
          ref={scrollRef}
          className={cn(listMaxHeight, 'space-y-0.5 overflow-y-auto rounded-md border border-border p-1')}
        >
          {searchResults.map((food, idx) => {
            const isSelected = cartKeys.has(dbCartKey(food))
            return (
              <label
                key={`${food.food_name}-${idx}`}
                className="flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 hover:bg-accent/50 transition-colors"
              >
                <Checkbox checked={isSelected} onCheckedChange={() => handleToggle(food)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium">{food.food_name}</span>
                    {food.shop_name && (
                      <span className="shrink-0 text-[10px] text-muted-foreground">({food.shop_name})</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex gap-2 text-[11px] text-muted-foreground">
                    <span className="tabular-nums">{food.calories}kcal</span>
                    <span className="tabular-nums text-blue-400">P{food.protein_g}</span>
                    <span className="tabular-nums text-yellow-400">F{food.fat_g}</span>
                    <span className="tabular-nums text-green-400">C{food.carb_g}</span>
                  </div>
                </div>
              </label>
            )
          })}
          <div ref={sentinelRef} className="h-px" />
          {isFetchingNextPage && (
            <div className="flex justify-center py-1.5">
              <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}

      {/* 空状態 */}
      {searchResults && searchResults.length === 0 && !hasNextPage && !isSearching && (
        <p className="py-2 text-center text-xs text-muted-foreground">一致する食品が見つかりませんでした</p>
      )}
    </div>
  )
}
